<template>
  <div>
    <div>
      <AvailableBalance
        class="sidebar__available-balance"
        :available-balance-data="unifiedAccountAvailableBalance"
        :error="hasBalancesBrokerRequestFailed"
        :loading="isBalancesBrokerRequestLoading"
        :has-margin-allocation="hasMarginAllocation"
        @on-retry="fetchBalances"
      />

      <TradeStatus
        v-if="isMarketNotAvailable"
        :status="statusMarketAvailable"
      />

      <PositionsSummarySection />

      <ServicesSection
        v-if="serviceButtons"
        :service-buttons="serviceButtons"
        @open-home-broker="onAccessHomeBrokerClick"
      />

      <WatchList
        :set-watchlist-subscription="getWatchlistSubscription"
        :set-panel="() => setWatchlistPanel(setPanel)"
      />
    </div>

    <GeneralFooter @help-click="handleHelpClick" />

    <FeeTransparencyDisclaimer />
  </div>
</template>

<script lang="ts">
import { Component, Prop } from 'vue-property-decorator';

import { ServicesSection } from '../ServicesSection';
import { WatchList } from '../WatchList';
import { TradeStatus } from './components/TradeStatus';
import { SidebarTypes } from '@/modules/trade/views/TradeView/components/TradeSidebar';
import { GeneralFooter } from './components/GeneralFooter';
import { PositionsSummarySection } from './components/PositionsSummarySection';
import FeeTransparencyDisclaimer from '@/modules/trade/views/TradeView/components/FeeTransparencyDisclaimer/FeeTransparencyDisclaimer.vue';
import { isImpersonate } from '@/modules/common/services/partner';
import { RouteMixin } from '@/modules/common/mixins/route';
import { mixins } from 'vue-class-component';
import { namespace } from 'vuex-class';
import { AccountStatus, IHeroBalances } from '@/modules/trade/services/account';
import {
  trackHomeBrokerClick,
  trackHelpClick,
  trackStatementAndNotesClick
} from '@/modules/trade/views/TradeView/trackers';
import { MFAModules } from '@/modules/authentication/views/SecurityView/components/MultiFactorAuthentication';
import { RequestStatus } from '@/modules/trade/store/trade';
import { AvailableBalance } from '@/modules/trade/components/AvailableBalance';
import { getTradeButtons, TradeButton } from '@/modules/trade/services/buttons';
import { useDashStore } from '@/modules/common/store/DashModule';

const tradePositionsModule = namespace('tradeModule');

@Component({
  components: {
    ServicesSection,
    WatchList,
    TradeStatus,
    GeneralFooter,
    PositionsSummarySection,
    AvailableBalance,
    FeeTransparencyDisclaimer
  }
})
export default class GeneralSidebarList extends mixins(RouteMixin) {
  @tradePositionsModule.State('userHasBrokerAccount')
  readonly userHasBrokerAccount!: boolean;

  @tradePositionsModule.State('balancesBrokerRequestStatus')
  readonly balancesBrokerRequestStatus!: RequestStatus;

  @tradePositionsModule.Action('fetchBalances')
  readonly fetchBalances!: Function;

  @Prop({ type: Object, required: false })
  readonly balances?: IHeroBalances;

  @Prop({ type: Object, required: false })
  readonly tradeStatus?: AccountStatus;

  @Prop({ type: Function, default: () => {} })
  readonly onAccessHomeBrokerClick!: Function;

  @Prop({ type: Function, required: true })
  readonly setPanel!: Function;

  @Prop({ type: Function, required: true })
  readonly getWatchlistSubscription!: () => {};

  @Prop({ type: Function, default: () => {} })
  readonly openBrokerModal!: Function;

  private isImpersonate = isImpersonate();

  private serviceButtons: TradeButton[] = [];

  readonly dashStore = useDashStore();

  private get homeBrokerButtonEnable() {
    if (this.isImpersonate || this.updatedAccountRegulation) {
      return false;
    }

    return true;
  }

  get unifiedAccountAvailableBalance() {
    return {
      availableOnAccountValue: this.balances?.account?.availableBalance,
      availableToOperateValue: this.balances?.account?.detail?.availableBalance,
      toSettleValue: this.balances?.account?.detail?.liquidatedBalance
    };
  }

  get hasBalancesBrokerRequestFailed() {
    return this.balancesBrokerRequestStatus === RequestStatus.Error;
  }

  get isBalancesBrokerRequestLoading() {
    return this.balancesBrokerRequestStatus === RequestStatus.Loading;
  }

  get isMarketNotAvailable() {
    return !this.tradeStatus?.marketAvailable?.isMarketAvailable;
  }

  get statusMarketAvailable() {
    return this.tradeStatus?.marketAvailable;
  }

  get isDarkMode() {
    return this.dashStore.isDarkMode;
  }

  /**
   * This is temporally, this panel transition will be change for a new all positions page
   */
  public setSubscriptionsPanel(setPanel: (param: SidebarTypes) => void) {
    setPanel(SidebarTypes.SubscriptionsList);
    window.scrollTo(0, 0);
  }

  /**
   * This is temporally, this panel transition will be change for a new all watchlist page
   */
  public setWatchlistPanel(setPanel: (param: SidebarTypes) => void) {
    setPanel(SidebarTypes.Watchlist);
    window.scrollTo(0, 0);
  }

  public goToSecurity() {
    trackHomeBrokerClick(
      'Ativar Home Broker - Cadastre a verificação em duas etapas'
    );
    this.$router.push({
      name: 'security',
      query: { module: MFAModules.TRADE }
    });
  }

  handleHelpClick() {
    trackHelpClick();
  }

  public trackAccountStatements() {
    trackStatementAndNotesClick();
  }

  private get updatedAccountRegulation() {
    return this.tradeStatus && !this.tradeStatus.enabledBuy;
  }

  private setSummaryPanels(panel: SidebarTypes) {
    this.setPanel(panel);
    window.scrollTo(0, 0);
  }

  private async getTradeButtons() {
    try {
      this.serviceButtons = await getTradeButtons();
    } catch (error) {
      console.error('Error getting trade buttons', error);
    }
  }

  private get hasMarginAllocation() {
    if (!this.serviceButtons) return false;
    return this.serviceButtons.some(
      button => button.name === 'marginAllocation' && button.enabled
    );
  }

  async mounted() {
    await this.getTradeButtons();
  }
}
</script>
<style lang="less" scoped>
.sidebar {
  &__available-balance {
    margin-bottom: @size-spacing-x800;
    width: 100%;
  }
}
</style>
